/**
 * V3: AI Content Generator Module
 * Handles slider synchronization and legal acknowledgement for 
 * both the Script Generator (ID 5) and Title Generator (ID 6).
 *
 * @module modules/v3_ai_content_generator
 */

// Dependencies
import { $ } from '../common/utility/u-jquery-$';

// Toggle for troubleshooting slider sync or AJAX transition issues
const DEBUG_MODE = false;

// We store user values here to prevent Gravity Forms from resetting 
// sliders to default when navigating multi-step AJAX forms.
const sliderPersistence = {
	'tone_of_voice_slider': '50', 
	'audience_knowledge_slider_input': '50'
};

/**
 * Targets the specific hidden GF input field.
 * We use .first() to prevent selectors from bleeding into neighboring 
 * fields (like Number of Presenters) if they share a container.
 */
function getTargetGFInputField(parentClassName) {
	return $(`.ai-event-script-generator-form_wrapper .${parentClassName}, .gform_wrapper .${parentClassName}`)
			.find('input[type="number"]')
			.first();
}

/**
 * Force-syncs the visual slider with the hidden GF field.
 * We trigger multiple events (change/keyup) to ensure GForms 
 * picks up the data for its internal serialization.
 */
function setValueAggressively($slider, $gfField, sliderId) {
	const sliderValue = $slider.val();
	
	sliderPersistence[sliderId] = sliderValue;

	$gfField.val(sliderValue);
	$gfField.trigger('change');
	$gfField.trigger('keyup'); 
	$gfField.attr('value', sliderValue);
	
	if (DEBUG_MODE) {
		console.log(`[Sync] ID=${sliderId} set to ${sliderValue}`);
	}
}

/**
 * Core sync logic. Maps visual sliders to the hidden number fields 
 * defined in the GF editor via custom CSS classes.
 */
function handleRangeSliderSync($context) {
	// Form 5 & Form 6 use the same visual ID for tone
	const $toneSlider = $context.find('#tone_of_voice_slider');
	
	// We attempt to find the hidden field for either form variant
	const $scriptToneGF = getTargetGFInputField('ai-gen-tone-value'); // Form 5
	const $titleToneGF = getTargetGFInputField('ai-gen-title-tone'); // Form 6

	// Audience knowledge is currently specific to the Script Generator
	const $knowledgeSlider = $context.find('#audience_knowledge_slider_input');
	const $knowledgeGF = getTargetGFInputField('ai-gen-knowledge-level-value');

	const initializeSliderSync = function($slider, $gfField, sliderId) {
		// Guard: Stop if elements are missing or if we're targeting a visible field by mistake
		if (!$slider.length || !$gfField.length || $gfField.is(':visible')) return false;

		// Re-apply stored value to maintain state during AJAX transitions
		const lastValue = sliderPersistence[sliderId] || '50';
		$slider.val(lastValue);
		
		if (!$gfField.prop('readonly')) {
			$gfField.prop('readonly', true);
		}

		// Avoid double-binding if the observer triggers multiple times
		if ($slider.data('listener-attached')) {
			setValueAggressively($slider, $gfField, sliderId);
			return true;
		}

		$slider.on('input', function() {
			setValueAggressively($(this), $gfField, sliderId);
		});

		setValueAggressively($slider, $gfField, sliderId);
		$slider.data('listener-attached', true);
		
		return true;
	};

	// Initialize whichever fields are found on the current page
	initializeSliderSync($toneSlider, $scriptToneGF, 'tone_of_voice_slider');
	initializeSliderSync($toneSlider, $titleToneGF, 'tone_of_voice_slider');
	initializeSliderSync($knowledgeSlider, $knowledgeGF, 'audience_knowledge_slider_input');
}

/**
 * Manages the legal disclaimer visibility and platform selection.
 */
function handleLegalAcknowledgement() {

	$(document).on('click', '.js-llm-trigger', function(e) {
		e.preventDefault();
		const $btn = $(this);
		const destinationUrl = $btn.attr('href');
		const platformName = $btn.data('platform');
		const termsUrl = $btn.data('terms');

		// We find the containers relative to the document or the specific wrapper
		const $step1 = $('#step-1-platform-selection');
		const $step2 = $('#step-2-legal-acknowledgement');
		const $legalText = $('#legal-text');
		const $confirmBtn = $('#js-confirm-proceed');
		const $platformSpan = $('#js-platform-name');

		// Build out the disclaimer text and link
		const disclaimer = `By choosing to submit your prompt to <strong>${platformName}</strong> you are stating that you have read and agree to the terms and conditions of ${platformName} on <a href="${termsUrl}" target="_blank" rel="noopener noreferrer">${termsUrl}</a>.`;
		
		$legalText.html(disclaimer);
		$platformSpan.text(platformName);
		$confirmBtn.attr('href', destinationUrl);

		// Toggle UI state
		$step1.hide();
		$step2.fadeIn(300);
	});

	// Delegated Back Button
	$(document).on('click', '#js-back-to-platforms', function(e) {
		e.preventDefault();
		$('#step-2-legal-acknowledgement').hide();
		$('#step-1-platform-selection').fadeIn(300);
	});

	// Delegated Revert view after the final proceed action
	$(document).on('click', '#js-confirm-proceed', function() {
		const $step1 = $('#step-1-platform-selection');
		const $step2 = $('#step-2-legal-acknowledgement');
		
		setTimeout(() => {
			$step2.hide();
			$step1.show();
		}, 800);
	});
}

/**
 * Core module initialization.
 *
 * @return {void}
 */
function _moduleInit() {
	const form = document.querySelector('.v3-ai-content-gen');
	const confirmation = document.querySelector('.v3-generator-confirmation');

	if ( !form ) {
		return;
	}

	handleLegalAcknowledgement();

	// Init UI components
	handleRangeSliderSync($(document));

	// Use MutationObserver for multi-page AJAX transition detection
	const formWrapper = document.querySelector('.ai-event-script-generator-form_wrapper'); 
	
	if (formWrapper) {
		if (DEBUG_MODE) {
			console.log('Observer: Initializing Mutation Observer to watch for page transitions.');
		}
		
		const observer = new MutationObserver((mutationsList, observer) => {
			for (const mutation of mutationsList) {
				if (mutation.type === 'childList') {
					if (DEBUG_MODE) {
						console.log('Observer: Detected DOM change (AJAX page transition). Re-syncing sliders and re-asserting values.');
					}
					// Run the synchronization logic again
					handleRangeSliderSync($(document));
					return; 
				}
			}
		});

		observer.observe(formWrapper, { childList: true, subtree: true });
		if (DEBUG_MODE) {
			console.log('Observer: Mutation Observer is active.');
		}
	}
}

// Auto Init
// ============================================================================
$(document).ready(() => _moduleInit());